import { Platform } from '@/types/generated/enums';

export type AdAccountPlatform = typeof Platform.GOOGLE | typeof Platform.META;

interface DataLayerEvent {
  event: string;
  [key: string]: unknown;
}

declare global {
  interface Window {
    // gtag.js reads a command only as an `arguments` object, never as an array.
    dataLayer?: (DataLayerEvent | IArguments)[];
    gtag?: (...args: unknown[]) => void;
  }
}

// One key per event id, shared by every push that carries one.
const PUSHED_KEY = 'coretas.datalayer_event';

// GTM only: a GTM trigger, never a GA4 event.
export const pushDataLayer = (event: DataLayerEvent): void => {
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(event);
};

export const gtagCommand = function () {
  window.dataLayer = window.dataLayer ?? [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
} as (...command: unknown[]) => void;

// GA4 is loaded exactly when `window.gtag` exists; nothing else defines it.
export const loadedGtag = (): ((...args: unknown[]) => void) | undefined =>
  typeof window.gtag === 'function' ? window.gtag : undefined;

// GTM keeps its dataLayer push; GA4 gets the event only once gtag is loaded.
export const trackEvent = (event: DataLayerEvent): void => {
  pushDataLayer(event);
  const { event: name, ...params } = event;
  loadedGtag()?.('event', name, params);
};

export interface AdPlatformConversion {
  platform: AdAccountPlatform;
  // The server's claim id: Meta matches this push with its Conversions API twin on it.
  eventId: string;
}

// One window can hear the same result twice, and storage can be unavailable; this always holds.
const pushedEvents = new Set<string>();

// GTM only, once per event id: the server reports these to GA4 and Meta itself.
const pushOnce = (event: DataLayerEvent & { event_id: string }): void => {
  const key = `${PUSHED_KEY}.${event.event_id}`;
  if (pushedEvents.has(key)) return;
  try {
    if (window.localStorage.getItem(key)) return;
    window.localStorage.setItem(key, '1');
  } catch {
    // Meta dedupes on the event id; this guard only spares GTM's other tags a repeat.
  }
  pushedEvents.add(key);
  pushDataLayer(event);
};

export const trackAdPlatformConnected = ({
  platform,
  eventId,
}: AdPlatformConversion): void =>
  pushOnce({ event: 'ad_platform_connected', platform, event_id: eventId });

export const trackSignUp = (eventId: string): void =>
  pushOnce({ event: 'sign_up', event_id: eventId });
